import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { QrCode, CreditCard, ShieldCheck, Loader2, Lock, BadgeCheck, HeartHandshake } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLang } from "@/i18n/LanguageContext";
import {
  QR_IMAGE, UPI_ID, confirmUpi, createVisitor, createOrder, verifyPayment, createSubscription,
} from "@/lib/api";

function loadScript(src) {
  return new Promise((resolve) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve(true);
    const s = document.createElement("script");
    s.src = src;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const Pill = ({ active, children, ...p }) => (
  <button
    {...p}
    className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
      active ? "bg-primary text-primary-foreground" : "bg-secondary/30 text-foreground hover:bg-secondary/50"
    }`}
  >
    {children}
  </button>
);

export const DonateSection = ({ campaigns, selected, donation }) => {
  const { t, lang } = useLang();
  const PRESETS = donation?.suggested_amounts?.length ? donation.suggested_amounts : [500, 1100, 2100, 5100];
  const options = [
    ...campaigns.map((c) => ({ label: `${c.cause} — ${c.title}`, cause: c.cause, campaign_id: c.id })),
    { label: t("donate.weakerSection"), cause: "Weaker Section Welfare", campaign_id: null },
    { label: t("donate.hindu"), cause: "Hindu Religious Empowerment", campaign_id: null },
    { label: t("donate.general"), cause: "General Fund", campaign_id: null },
  ];

  const [causeIdx, setCauseIdx] = useState(0);
  const [frequency, setFrequency] = useState("one-time");
  const [method, setMethod] = useState("upi");
  const [amount, setAmount] = useState(1100);
  const amountTouched = useRef(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);

  const [visitor, setVisitor] = useState({ name: "", email: "", phone: "", message: "" });
  const [vLoading, setVLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const hi = lang === "hi";

  const upiLink = (() => {
    const n = Number(amount);
    const base = `upi://pay?pa=${UPI_ID}&pn=${encodeURIComponent("Findivine")}&cu=INR&tn=${encodeURIComponent("Donation to Findivine")}`;
    return Number.isFinite(n) && n > 0 ? `${base}&am=${n}` : base;
  })();

  const copyUpiId = async () => {
    try {
      await navigator.clipboard.writeText(UPI_ID);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = UPI_ID;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* unsupported */ }
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    if (selected) {
      const i = options.findIndex((o) => o.campaign_id === selected.id);
      if (i >= 0) setCauseIdx(i);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Snap the default amount to a suggested preset once CMS settings load
  useEffect(() => {
    if (!amountTouched.current && PRESETS.length && !PRESETS.includes(Number(amount))) {
      setAmount(PRESETS[Math.min(1, PRESETS.length - 1)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donation]);

  const donor = () => ({
    ...form,
    cause: options[causeIdx].cause,
    campaign_id: options[causeIdx].campaign_id,
    amount: Number(amount),
    frequency,
    method,
  });

  const valid = () => {
    if (!form.name || !form.email || !form.phone) {
      toast.error(t("donate.fillDetails"));
      return false;
    }
    if (!amount || Number(amount) < 1) {
      toast.error(t("donate.validAmount"));
      return false;
    }
    return true;
  };

  const handleUpi = async () => {
    if (!valid()) return;
    setLoading(true);
    try {
      await confirmUpi(donor());
      toast.success(t("donate.thanksDonation"));
      setForm({ name: "", email: "", phone: "" });
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCard = async () => {
    if (!valid()) return;
    setLoading(true);
    try {
      if (frequency === "monthly") {
        const res = await createSubscription({ amount: Number(amount), donor: donor() });
        if (res.short_url) window.open(res.short_url, "_blank");
      } else {
        const ok = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
        if (!ok) throw new Error("script");
        const order = await createOrder({ amount: Number(amount), donor: donor() });
        const rzp = new window.Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: "INR",
          name: "Findivine — One Kind Earth Foundation",
          description: options[causeIdx].cause,
          order_id: order.order_id,
          prefill: { name: form.name, email: form.email, contact: form.phone },
          theme: { color: "#c85a30" },
          handler: async (r) => {
            await verifyPayment({ ...r, donation_id: order.donation_id });
            toast.success(t("donate.paySuccess"));
            setForm({ name: "", email: "", phone: "" });
          },
        });
        rzp.open();
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 503) {
        toast.info(t("donate.cardActivating"));
        setMethod("upi");
      } else {
        toast.error("Could not start payment. Please use the UPI QR.");
      }
    } finally {
      setLoading(false);
    }
  };

  const submitVisitor = async (e) => {
    e.preventDefault();
    if (!visitor.name || !visitor.email || !visitor.phone) {
      toast.error(t("donate.fillDetails"));
      return;
    }
    setVLoading(true);
    try {
      await createVisitor(visitor);
      toast.success(t("donate.thanksVisitor"));
      setVisitor({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setVLoading(false);
    }
  };

  const submitLabel = method === "upi"
    ? t("donate.paidUpi").replace("{a}", amount || 0)
    : frequency === "monthly"
    ? t("donate.setupMonthly").replace("{a}", amount || 0)
    : t("donate.donateSecure").replace("{a}", amount || 0);

  return (
    <section id="donate" data-testid="donate-section" className="py-28 bg-secondary/15">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-10">
          <p className="text-xs tracking-[0.3em] uppercase font-bold text-primary mb-5">{t("donate.overline")}</p>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-foreground leading-tight">
            {t("donate.title")}
          </h2>
          <div className="mt-6 flex flex-wrap gap-x-7 gap-y-3 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-2"><Lock size={15} className="text-primary" /> Secure payments</span>
            <span className="inline-flex items-center gap-2"><BadgeCheck size={15} className="text-primary" /> Acknowledgement emailed</span>
            <span className="inline-flex items-center gap-2"><HeartHandshake size={15} className="text-primary" /> 100% to the cause</span>
          </div>
          <p className="mt-5 text-sm text-muted-foreground leading-relaxed max-w-2xl">
            Your donation is a voluntary contribution to Findivine (One Kind Earth Foundation), an NGO working for
            the welfare of society. It supports our stated social welfare objectives — women empowerment, animal
            welfare, child education, weaker-section welfare and religious empowerment.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="lg:col-span-3 bg-card border border-border rounded-2xl p-7 sm:p-10">
            <div className="flex flex-wrap gap-3 mb-8">
              <Pill data-testid="freq-onetime" active={frequency === "one-time"} onClick={() => setFrequency("one-time")}>{t("donate.oneTime")}</Pill>
              <Pill data-testid="freq-monthly" active={frequency === "monthly"} onClick={() => setFrequency("monthly")}>{t("donate.monthly")}</Pill>
            </div>

            <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("donate.chooseCause")}</Label>
            <select
              data-testid="cause-select"
              value={causeIdx}
              onChange={(e) => setCauseIdx(Number(e.target.value))}
              className="mt-2 mb-8 w-full rounded-xl border border-input bg-white/60 px-4 py-3 text-base md:text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {options.map((o, i) => <option key={i} value={i}>{o.label}</option>)}
            </select>

            <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("donate.amount")}</Label>
            <div className="mt-2 mb-3 flex flex-wrap gap-3">
              {PRESETS.map((a) => (
                <Pill key={a} data-testid={`amount-${a}`} active={Number(amount) === a} onClick={() => setAmount(a)}>₹{a}</Pill>
              ))}
            </div>
            <Input
              data-testid="amount-input" type="number" min="1" value={amount}
              onChange={(e) => { amountTouched.current = true; setAmount(e.target.value); }}
              className="mb-8 bg-white/60" placeholder={t("donate.custom")} />

            <div className="grid sm:grid-cols-2 gap-4 mb-2">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("donate.fullName")}</Label>
                <Input data-testid="donor-name" className="mt-2 bg-white/60" value={form.name}
                       onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("donate.yourName")} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("donate.phone")}</Label>
                <Input data-testid="donor-phone" className="mt-2 bg-white/60" value={form.phone}
                       onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91" />
              </div>
            </div>
            <div className="mb-8">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("donate.email")}</Label>
              <Input data-testid="donor-email" type="email" className="mt-2 bg-white/60" value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" />
            </div>

            <div className="flex flex-wrap gap-3 mb-8">
              <Pill data-testid="method-upi" active={method === "upi"} onClick={() => setMethod("upi")}>
                <span className="inline-flex items-center gap-2"><QrCode size={16} /> {t("donate.methodUpi")}</span>
              </Pill>
              <Pill data-testid="method-card" active={method === "card"} onClick={() => setMethod("card")}>
                <span className="inline-flex items-center gap-2"><CreditCard size={16} /> {t("donate.methodCard")}</span>
              </Pill>
            </div>

            <button
              data-testid="donate-submit"
              disabled={loading}
              onClick={method === "upi" ? handleUpi : handleCard}
              className="w-full rounded-full bg-primary text-primary-foreground py-4 text-sm font-semibold hover:scale-[1.01] transition-transform disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {submitLabel}
            </button>
            <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck size={14} className="text-primary" /> {t("donate.ackNote")}
            </p>
            <div data-testid="donate-disclosures" className="mt-4 border-t border-border pt-4 space-y-2 text-xs text-muted-foreground leading-relaxed">
              <p>
                No additional charges — the amount shown above is the exact amount debited. Donations are subject to
                successful processing and confirmation by the payment provider.
              </p>
              <p>
                By donating you agree to our{" "}
                <a href="/terms-and-conditions" data-testid="donate-terms-link" className="text-primary underline underline-offset-2">Terms &amp; Conditions</a>,{" "}
                <a href="/refund-policy" data-testid="donate-refund-link" className="text-primary underline underline-offset-2">Refund &amp; Cancellation Policy</a> and{" "}
                <a href="/privacy-policy" data-testid="donate-privacy-link" className="text-primary underline underline-offset-2">Privacy Policy</a>.
              </p>
              <p>
                Payment failed, pending, or debited by mistake? Contact{" "}
                <a href="mailto:info@findivine.org" data-testid="donate-help-email" className="text-primary underline underline-offset-2">info@findivine.org</a>{" "}
                or call{" "}
                <a href="tel:+918875285537" data-testid="donate-help-phone" className="text-primary underline underline-offset-2">+91 8875285537</a>.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: 0.1 }} className="lg:col-span-2 flex flex-col gap-8">
            <div className="bg-[hsl(160_38%_16%)] text-[hsl(42_33%_96%)] rounded-2xl p-7 text-center">
              <p className="text-xs tracking-[0.3em] uppercase font-bold text-secondary mb-4">{t("donate.scanTitle")}</p>
              <a href={upiLink} data-testid="upi-qr-link" title={hi ? "UPI ऐप से भुगतान करने हेतु टैप करें" : "Tap to pay via UPI app"} className="bg-white rounded-xl p-4 inline-block">
                <img data-testid="upi-qr" src={QR_IMAGE} alt="Findivine UPI QR" className="w-52 h-52 object-contain" />
              </a>
              <p className="mt-5 text-sm text-white/80">{t("donate.scanDesc")}</p>
              <a href={upiLink} data-testid="upi-pay-btn"
                 className="mt-5 block w-full rounded-full bg-primary text-primary-foreground py-3.5 text-sm font-semibold hover:scale-[1.02] transition-transform">
                {hi ? "UPI ऐप से भुगतान करें" : "Pay via UPI App"}
              </a>
              <button type="button" data-testid="upi-copy-btn" onClick={copyUpiId}
                      className="mt-3 w-full rounded-full border border-white/25 py-3 text-sm font-medium hover:bg-white/10 transition-colors">
                {copied ? (hi ? "कॉपी हो गया ✓" : "Copied ✓") : (hi ? "UPI ID कॉपी करें" : "Copy UPI ID")}
              </button>
              <p data-testid="upi-id-text" className="mt-3 text-xs font-mono text-white/70 select-all">{UPI_ID}</p>
              <p className="mt-4 text-xs text-white/60 leading-relaxed text-left">
                {hi
                  ? "अपने फ़ोन से दान कर रहे हैं? ऊपर “UPI ऐप से भुगतान करें” पर टैप करें और Google Pay, PhonePe या Paytm चुनें — राशि पहले से भरी होगी। या UPI ID कॉपी करके अपने ऐप के “Pay anyone” में पेस्ट करें। दूसरे डिवाइस से भुगतान कर रहे हैं? QR कोड स्कैन करें। भुगतान के बाद दान फ़ॉर्म में अपनी जानकारी ज़रूर भरें ताकि हम आपकी पावती भेज सकें।"
                  : "Donating from your phone? Tap “Pay via UPI App” above and choose Google Pay, PhonePe or Paytm — the amount is filled in for you. Or copy our UPI ID and paste it in your app’s “Pay anyone” option. Paying from a second device? Scan the QR code. After paying, fill in your details in the donation form so we can send your acknowledgement."}
              </p>
            </div>

            <form onSubmit={submitVisitor} data-testid="visitor-form"
                  className="bg-card border border-border rounded-2xl p-7">
              <h3 className="font-serif text-2xl text-foreground mb-1">{t("donate.notReady")}</h3>
              <p className="text-sm text-muted-foreground mb-5">{t("donate.notReadyDesc")}</p>
              <div className="space-y-3">
                <Input data-testid="visitor-name" placeholder={t("donate.yourName")} className="bg-white/60" value={visitor.name}
                       onChange={(e) => setVisitor({ ...visitor, name: e.target.value })} />
                <Input data-testid="visitor-email" type="email" placeholder={t("donate.email")} className="bg-white/60" value={visitor.email}
                       onChange={(e) => setVisitor({ ...visitor, email: e.target.value })} />
                <Input data-testid="visitor-phone" placeholder={t("donate.phone")} className="bg-white/60" value={visitor.phone}
                       onChange={(e) => setVisitor({ ...visitor, phone: e.target.value })} />
                <Input data-testid="visitor-message" placeholder={t("donate.message")} className="bg-white/60" value={visitor.message}
                       onChange={(e) => setVisitor({ ...visitor, message: e.target.value })} />
                <button data-testid="visitor-submit" disabled={vLoading}
                        className="w-full rounded-full border border-primary text-primary py-3 text-sm font-semibold hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-60">
                  {vLoading ? t("donate.sending") : t("donate.stayConnected")}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
